import { Button } from "@components/button";
import { Modal } from "@components/modal";
import { PaymentProcessing } from "@features/appeals/components/status/index";
import { formatDate, formatTime } from "@helpers/date";
import { formatRubles, formatUsdt } from "@helpers/format";
import styles from "./modal.module.scss";
import { useAppeals } from "@features/appeals";
import React from "react";
import { useNotification } from "@hooks/use-notification";
import { MergedAppeal } from "@features/appeals/slice";
import { EllipsisText } from "@components/ellipsis-text";
import { RowTable } from "@components/row-table";
import { Config, DoubleText } from "@components/table";
import { StatusBlock, StatusBlockType } from "@components/status-block";

type Props = {
  appeal?: MergedAppeal | null;
  onClose: () => void;
};

export const RequestModal = ({ appeal, onClose }: Props) => {
  const { updateAppeal, getAppeals } = useAppeals();
  const notif = useNotification();
  const config: Config<MergedAppeal> = [
    {
      label: "Номер апелляции",
      source: "id",
      renderComponent: (data) => (
        <EllipsisText text={data} copy startLength={10} />
      ),
    },
    {
      label: "Сумма заявки",
      source: "order_start_amount",
      renderComponent: (cell, item) => {
        if (cell == null || item.order_start_amount_usdt == null) return "";
        const rub = formatRubles(cell);
        const usdt = formatUsdt(item.order_start_amount_usdt);
        return <DoubleText text={rub} subText={usdt} />;
      },
    },
    {
      label: "Сумма изменена",
      source: "merchant_amount",
      renderComponent: (merchant_amount) =>
        merchant_amount != null ? "Да" : "Нет",
      visible: (_, item) => item.order_type == "pay_in",
    },
    {
      label: "Сумма мерчанта",
      source: "merchant_amount",
      renderComponent: (cell, item) => {
        if (cell == null || item.merchant_amount_usdt == null) return "";
        const rub = formatRubles(cell);
        const usdt = formatUsdt(item.merchant_amount_usdt);
        return <DoubleText text={rub} subText={usdt} />;
      },
      visible: (_, item) => item.order_type == "pay_in",
    },
    {
      label: "Итоговая сумма",
      source: "order_final_amount",
      renderComponent: (cell, item) => {
        if (cell == null || item.order_final_amount_usdt == null) return "";
        const rub = formatRubles(cell);
        const usdt = formatUsdt(item.order_final_amount_usdt);
        return <DoubleText text={rub} subText={usdt} />;
      },
      visible: (_, item) => item.order_type == "pay_in",
    },
    { label: "Реквизит", source: "card_number" },
    { label: "Банк", source: "bank" },
    // {
    //   label: "ФИО",
    //   source: "cardholder_name",
    // },
    {
      source: "exchange_rate",
      label: "Курс",
      renderComponent: (data) => formatUsdt(data, true),
    },
    {
      label: "Статус заявки",
      source: "order_status",
      renderComponent: (status) => {
        let text = "";
        let statusType: StatusBlockType;
        switch (status) {
          case "canceled":
            text = "Отклонена";
            statusType = StatusBlockType.Error;
            break;
          case "completed":
            text = "Успешна";
            statusType = StatusBlockType.Success;
            break;
          case "dispute_open":
            text = "Открыт спор";
            statusType = StatusBlockType.Warning;
            break;
          case "pending":
          case "processing":
          default:
            text = "Ожидает подтверждения";
            statusType = StatusBlockType.Warning;
            break;
        }
        return <StatusBlock text={text} type={statusType} />;
      },
    },
    // {
    //   label: "Сообщение чекера",
    //   source: "order_checker_message",
    //   renderComponent: (order_checker_message) => order_checker_message || "Отсутствует",
    // },
    // { label: "Устройство", source: "device_name" },
    { label: "Сообщение от мерчанта", source: "message" },
    {
      label: "Дата и время апелляции",
      source: "created_at",
      renderComponent: (dateTime) => {
        const time = formatTime(new Date(dateTime));
        const date = formatDate(new Date(dateTime));
        return <DoubleText text={time} subText={date} />;
      },
    },
    {
      label: "Дата и время заявки",
      source: "order_created_at",
      renderComponent: (dateTime) => {
        const time = formatTime(new Date(dateTime));
        const date = formatDate(new Date(dateTime));
        return <DoubleText text={time} subText={date} />;
      },
    },
    {
      source: "check_image_links",
      label: "Файл",
      renderComponent: (data) =>
        data &&
        data.map((file) => (
          <React.Fragment key={file}>
            <a className="underline" href={file} target="_blank">
              {file.split("/").pop()}
            </a>
            <br />
          </React.Fragment>
        )),
    },
  ];
  const onReject = () => {
    if (appeal) {
      updateAppeal(appeal.id, {
        approve: false,
      }).then(() => {
        notif.addNotification("Аппеляция обновлена");
        getAppeals({ page: 1, limit: 10 });
        onClose();
      });
    }
  };

  const onAccept = () => {
    if (appeal) {
      updateAppeal(appeal.id, {
        approve: true,
      }).then(() => {
        notif.addNotification("Аппеляция обновлена");
        getAppeals({ page: 1, limit: 10 });
        onClose();
      });
    }
  };

  if (!appeal) {
    return null;
  }

  return (
    <Modal
      className={styles.modal}
      isOpen={true}
      onClose={onClose}
      title={`Заявка № ${appeal.order_uuid}`}
    >
      {/* {appeal.status === "approved" && <PaymentCredited />} */}
      {/* {appeal.status === "canceled" && <PaymentNotCredited />} */}
      {["created", "pending_support", "processing"].includes(appeal.status) && (
        <PaymentProcessing />
      )}
      <RowTable config={config} data={appeal} />
      {appeal.status != "approved" &&
        appeal.status != "canceled" &&
        appeal.merchant_amount == null && (
          <div className={styles.modalFooter}>
            {appeal.status != "processing" &&
              appeal.status != "pending_support" && (
                <Button type="outline" onClick={onReject}>
                  Отклонить
                </Button>
              )}
            <Button onClick={onAccept}>Принять</Button>
          </div>
        )}
    </Modal>
  );
};
